import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {Icon, IconProps} from '@primer/octicons-react'
import {type CSSProperties, type ReactNode, Suspense} from 'react'

import type {OcticonName} from './lazy-octicons-map'

/** A valid `@primer/octicons-react` icon component name, e.g. `"AlertIcon"`. */
export type LazyOcticonName = OcticonName

export type LazyOcticonProps = {
  /** The octicon component name to load, e.g. `"AlertIcon"`. */
  name: LazyOcticonName
  /**
   * Rendered while the icon chunk loads, and if the icon fails to resolve.
   * Defaults to a blank box sized to `size` so the icon does not shift layout
   * when it appears. Pass `null` to reserve no space.
   */
  fallback?: ReactNode
} & IconProps

/**
 * A per-name octicon map. On the client (`lazy-octicons-map.ts`) each entry is a
 * preloadable lazy component that code-splits its icon into its own chunk; on the
 * server (`LazyOcticon.server.tsx`) each entry is the real icon component so it
 * renders synchronously. The package's `node` export condition picks the server
 * build, so Alloy SSR never loads the code-split map.
 */
export type OcticonsMap = Record<OcticonName, Icon | (Icon & {preload: () => Promise<void>})>

export type LazyOcticonApi = {
  LazyOcticon: ((props: LazyOcticonProps) => ReactNode) & {preload: ({name}: {name: LazyOcticonName}) => Promise<void>}
  isLazyOcticonName: (name: string) => name is LazyOcticonName
  octiconComponent: (name: LazyOcticonName) => Icon
}

const SIZE_KEYWORDS = {small: 16, medium: 32, large: 64} as const

function toPixels(size: IconProps['size']): number {
  if (typeof size === 'number') return size
  if (typeof size === 'string' && size in SIZE_KEYWORDS) return SIZE_KEYWORDS[size]
  return 16
}

function PlaceholderBox({size}: {size: number}) {
  const style: CSSProperties = {
    display: 'inline-block',
    width: size,
    height: size,
    verticalAlign: 'text-bottom',
  }
  return <span aria-hidden style={style} />
}

/**
 * Builds the LazyOcticon API bound to a specific octicon map. The client and
 * server entrypoints (`LazyOcticon.tsx` / `LazyOcticon.server.tsx`) call this
 * with their respective maps — lazy code-split components on the client, real
 * synchronous components on the server — so the shared rendering logic lives in
 * one place and behaves identically apart from how each icon resolves.
 */
export function createLazyOcticon(octiconsMap: OcticonsMap): LazyOcticonApi {
  /**
   * Renders a single `@primer/octicons-react` icon by name.
   *
   * Use this when the icon is only known at runtime (e.g. from a CMS or API
   * payload) so callers avoid importing the entire octicons set up front. On the
   * client each distinct icon is code-split into its own chunk and fetched on
   * demand, with a correctly-sized fallback to avoid layout shift; on the server
   * it renders synchronously. If the icon cannot be resolved, the fallback is
   * rendered instead of throwing, and the failure is reported.
   */
  function LazyOcticon({name, fallback, size = 16, ...iconProps}: LazyOcticonProps) {
    const IconComponent = octiconsMap[name]
    const placeholder = fallback === undefined ? <PlaceholderBox size={toPixels(size)} /> : fallback

    // Deliberately no `onError`: supplying one marks this boundary as self-reporting
    // and suppresses the root's centralized reporting. An icon that never resolves
    // means a missing chunk or an invalid name, which is worth knowing about even
    // though the placeholder keeps the surrounding UI intact.
    return (
      <ErrorBoundary boundaryName="LazyOcticon" fallback={placeholder}>
        <Suspense fallback={placeholder}>
          <IconComponent size={size} {...iconProps} />
        </Suspense>
      </ErrorBoundary>
    )
  }

  LazyOcticon.preload = ({name}: {name: LazyOcticonName}) => {
    const IconComponent = octiconsMap[name]
    if (IconComponent && 'preload' in IconComponent && typeof IconComponent.preload === 'function') {
      return IconComponent.preload()
    }
    return Promise.resolve()
  }

  // Stable per-name wrapper components, so APIs that expect an octicon *component*
  // reference can share one instance per name across renders (no remount churn).
  const lazyIconComponentCache = new Map<LazyOcticonName, Icon>()

  /**
   * Type guard reporting whether an arbitrary string is a loadable octicon name.
   * Useful when resolving names from untrusted sources (CMS/API payloads) before
   * calling `octiconComponent` or rendering `LazyOcticon`.
   */
  function isLazyOcticonName(name: string): name is LazyOcticonName {
    return Object.hasOwn(octiconsMap, name)
  }

  /**
   *
   * This API is designed for backward compatibility with existing octicon consumers that expect a component
   * reference rather than rendered JSX. It provides a stable component for each icon name, which forwards props to `LazyOcticon` and reserves space while the icon chunk loads.
   * Eventually we may deprecate this and migrate those consumers to alternative structures.
   *
   * Returns a stable octicon *component* for the given name, backed by
   * `LazyOcticon`. Use this for third-party APIs that expect an octicon
   * component reference rather than rendered JSX — e.g. `@primer/react-brand`'s
   * `Card.Icon` `icon` prop or a list item's `leadingVisual`.
   *
   * The returned component forwards icon props (size, fill, etc.) and, while the
   * icon chunk loads, reserves space with a correctly-sized placeholder so the
   * icon does not shift layout when it appears. If you need a different loading
   * fallback (or none), render `LazyOcticon` directly with its `fallback`.
   */
  function octiconComponent(name: LazyOcticonName): Icon {
    const cached = lazyIconComponentCache.get(name)
    if (cached) return cached
    const Component: Icon = props => <LazyOcticon name={name} {...props} />
    Component.displayName = `LazyOcticon(${name})`
    lazyIconComponentCache.set(name, Component)
    return Component
  }

  return {LazyOcticon, isLazyOcticonName, octiconComponent}
}
